import { mergeFilterLeaves } from '@/features/companion/merge-filter-leaves'
import { leafSignature } from './extract-filter-summary'

/**
 * Provenance spine for History filter leaves.
 *
 * Every leaf the page adds through an interactive trigger — the Sankey drill, a
 * universal chart drill, the ask bar, or a "days like this" cohort — is tagged
 * with its `source` so the header can style/label the resulting chip and so the
 * page-load reset can strip the transient (session-scoped) ones. All four
 * triggers funnel through {@link applyProvenancedLeaves}, and the AI's
 * set_filters through {@link setProvenancedLeaves}; nothing else writes
 * provenance. This generalizes the original Sankey-only `sankeyLeaves` model.
 */

/** Where a provenanced leaf came from. */
export type LeafSource = 'sankey' | 'drill' | 'ask' | 'similar'

/** Provenance for a single leaf: how it was added and a chip label. */
export interface LeafMeta {
  source: LeafSource
  label: string
  /**
   * Workspace panel id the leaf was drilled from (the bar / cell / flow the
   * user double-clicked). The panel frame highlights every panel that still
   * owns a leaf here, so the drill's origin stays visible while its chip is
   * active. Absent for the ask bar and "days like this", which have no panel.
   */
  panelId?: string
}

/**
 * Map from `leafSignature(column, operator, value)` → its provenance. Stored
 * alongside `filters` in the history settings blob so it persists with them.
 */
export type LeafProvenance = Record<string, LeafMeta>

/**
 * A query-format leaf node, the shape drills append to the top-level AND:
 * `{ left: { column }, operator, right: { value } | { column } }`.
 */
export interface ProvenancedLeaf {
  left: { column: string }
  operator: string
  right: { value?: unknown; column?: string }
}

/**
 * Sources whose leaves are ephemeral, session-scoped selections: they narrow
 * the page while you interact but are stripped on the next page load (the
 * `SessionDrillResetBridge` behavior). `ask` and `similar` are authored queries
 * and persist.
 */
export const SESSION_SCOPED_SOURCES: ReadonlySet<LeafSource> = new Set<LeafSource>([
  'sankey',
  'drill',
])

/** Signature of a query-format leaf (mirrors `queryLeafSignature`). */
function signatureOf(leaf: ProvenancedLeaf): string {
  const value = 'value' in leaf.right ? leaf.right.value : leaf.right.column
  return leafSignature(leaf.left.column, leaf.operator, value)
}

/** Signature of an arbitrary top-level AND node, or null if it isn't a leaf. */
function nodeSignature(node: unknown): string | null {
  if (!node || typeof node !== 'object') return null
  const n = node as {
    left?: { column?: string }
    operator?: string
    right?: { value?: unknown; column?: string }
  }
  if (n.left?.column == null || n.operator == null || n.right == null) return null
  const value = 'value' in n.right ? n.right.value : n.right.column
  return leafSignature(n.left.column, n.operator, value)
}

/**
 * Normalize any accepted filter shape (undefined / array / single leaf / group)
 * into the list of nodes that live in the top-level AND — the sole place drills
 * add to. Nested groups inside that list are returned verbatim (untouched).
 */
function topLevelAnd(filters: unknown): unknown[] {
  if (filters == null) return []
  if (Array.isArray(filters)) {
    // Form format wraps in an array: [{ AND: [...] }]. Flatten each group's AND;
    // a bare leaf in the array is kept as-is.
    const out: unknown[] = []
    for (const child of filters) {
      if (child && typeof child === 'object' && Array.isArray((child as { AND?: unknown }).AND)) {
        out.push(...((child as { AND: unknown[] }).AND ?? []))
      } else if (child != null) {
        out.push(child)
      }
    }
    return out
  }
  if (typeof filters === 'object') {
    const rec = filters as Record<string, unknown>
    if (Array.isArray(rec.AND)) return [...(rec.AND as unknown[])]
    // A single leaf (or an OR group) at the root becomes the sole AND member.
    return [filters]
  }
  return []
}

/**
 * The provenance entry for `meta`. `panelId` only when present, so panel-less
 * sources persist the same two-field shape they always did.
 */
function entryFor(meta: LeafMeta): LeafMeta {
  return meta.panelId != null
    ? { source: meta.source, label: meta.label, panelId: meta.panelId }
    : { source: meta.source, label: meta.label }
}

/**
 * Apply provenanced leaves to the shared page filters with toggle semantics.
 *
 * For each incoming leaf: if a leaf with the same signature is already present
 * AND carries the same `source` in `provenance`, it is removed (click-again-to-
 * undo, generalizing the Sankey double-click). Otherwise it is appended to the
 * top-level AND and (re)tagged with `meta`. Nested groups are never touched.
 *
 * @returns the new `filters` (always an `{ AND: [...] }` group) and the updated
 *   `leafProvenance` map. Neither input is mutated.
 */
export function applyProvenancedLeaves(
  filters: unknown,
  provenance: LeafProvenance,
  leaves: ProvenancedLeaf[],
  meta: LeafMeta,
): { filters: Record<string, unknown>; leafProvenance: LeafProvenance } {
  const and = topLevelAnd(filters)
  const nextProvenance: LeafProvenance = { ...provenance }

  // Signatures to remove (toggle-off): incoming leaf already present under the
  // same source. Everything else is appended.
  const removeSigs = new Set<string>()
  const toAppend: ProvenancedLeaf[] = []

  for (const leaf of leaves) {
    const sig = signatureOf(leaf)
    const existing = provenance[sig]
    if (existing && existing.source === meta.source) {
      removeSigs.add(sig)
      delete nextProvenance[sig]
    } else {
      toAppend.push(leaf)
      nextProvenance[sig] = entryFor(meta)
    }
  }

  const kept = and.filter((node) => {
    const sig = nodeSignature(node)
    return sig == null || !removeSigs.has(sig)
  })

  return { filters: { AND: [...kept, ...toAppend] }, leafProvenance: nextProvenance }
}

/**
 * Set leaves on the shared page filters by column: the AI's set_filters, which
 * works the same on every surface (spec 2026-10-18 AI companion growth
 * architecture, 4.4). Each column a leaf names replaces that column's top-level
 * leaves, whatever their operator; every other node stays, nested groups whole.
 * Not a toggle: the same leaf set twice stays one. The replaced leaves'
 * provenance goes with them, and the new leaves are tagged with `meta`.
 *
 * @returns the new `filters` (always an `{ AND: [...] }` group, the shape the
 *   drills add to) and the updated `leafProvenance`. Neither input is mutated.
 */
export function setProvenancedLeaves(
  filters: unknown,
  provenance: LeafProvenance,
  leaves: ProvenancedLeaf[],
  meta: LeafMeta,
): { filters: Record<string, unknown>; leafProvenance: LeafProvenance } {
  const before = topLevelAnd(filters)
  const next = mergeFilterLeaves({ AND: before }, leaves) as { AND: unknown[] }
  // The merge keeps every node it does not replace as it was, so a node missing
  // from its result is one it replaced.
  const kept = new Set(next.AND)
  const nextProvenance: LeafProvenance = { ...provenance }
  for (const node of before) {
    const sig = kept.has(node) ? null : nodeSignature(node)
    if (sig) delete nextProvenance[sig]
  }
  for (const leaf of leaves) nextProvenance[signatureOf(leaf)] = entryFor(meta)
  return { filters: next, leafProvenance: nextProvenance }
}

/**
 * Panel ids that currently own at least one provenanced leaf, sorted and
 * deduped. The workspace frames read this to highlight where a drill came
 * from; the order is stable so callers can join it into a memo key.
 */
export function drillSourcePanelIds(provenance: LeafProvenance): string[] {
  const ids = new Set<string>()
  for (const meta of Object.values(provenance)) {
    if (meta.panelId) ids.add(meta.panelId)
  }
  return [...ids].sort()
}

/**
 * Cross-filter split for the panel a drill came from. Every OTHER panel narrows
 * to the drilled selection, but the origin panel keeps its full picture and
 * emphasises the selection instead (narrowing a by-date chart to its own
 * drilled week leaves one bar, which says nothing). Returns the page filters
 * minus the drill leaves `panelId` owns, plus those leaves so the panel can
 * highlight them. Only the top-level AND is read, the sole place drills add to;
 * a panel that owns nothing gets `filters` back unchanged (same reference).
 */
export function splitPanelDrill<T extends Record<string, unknown>>(
  filters: T,
  provenance: LeafProvenance,
  panelId: string,
): { filters: T; ownLeaves: ProvenancedLeaf[] } {
  const and = (filters as { AND?: unknown }).AND
  if (!Array.isArray(and)) return { filters, ownLeaves: [] }
  const ownLeaves: ProvenancedLeaf[] = []
  const kept = and.filter((node) => {
    const sig = nodeSignature(node)
    const meta = sig == null ? undefined : provenance[sig]
    if (meta?.source !== 'drill' || meta.panelId !== panelId) return true
    ownLeaves.push(node as ProvenancedLeaf)
    return false
  })
  if (ownLeaves.length === 0) return { filters, ownLeaves }
  return { filters: { ...filters, AND: kept }, ownLeaves }
}
